import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';

const NavItem = ({ to, icon, label, isActive, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
    style={isActive ? {
      background: 'rgba(99,102,241,0.25)',
      color: '#a5b4fc',
    } : {
      color: '#94a3b8',
    }}
    onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#e2e8f0'; }}
    onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#94a3b8'; }}
  >
    <span className="text-lg leading-none">{icon}</span>
    <span>{label}</span>
    {isActive && (
      <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#818cf8' }} />
    )}
  </Link>
);

const Layout = ({ children }) => {
  const { user, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isOwner = userRole === ROLES.BUSINESS_OWNER;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isClient = userRole === ROLES.CLIENT;

  const roleLabel = {
    [ROLES.BUSINESS_OWNER]: 'Business Owner',
    [ROLES.ACCOUNTANT]: 'Accountant',
    [ROLES.CLIENT]: 'Client',
  }[userRole] || '';

  const roleBadgeStyle = {
    [ROLES.BUSINESS_OWNER]: { background: 'rgba(99,102,241,0.2)', color: '#a5b4fc' },
    [ROLES.ACCOUNTANT]: { background: 'rgba(168,85,247,0.2)', color: '#c4b5fd' },
    [ROLES.CLIENT]: { background: 'rgba(20,184,166,0.2)', color: '#5eead4' },
  }[userRole] || {};

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex min-h-screen" style={{ background: '#f8fafc', overflowX: 'hidden' }}>
      {/* Sidebar */}
      <aside className="sidebar">
        {/* Logo */}
        <div className="p-5 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <Link to={isClient ? '/client-portal' : '/dashboard'} className="flex items-center gap-3" data-testid="logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#6366f1" className="w-8 h-8">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span className="text-white font-bold text-xl tracking-tight">Invoiceflow</span>
          </Link>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {isClient ? (
            <>
              <NavItem to="/client-portal" icon="📄" label="My Invoices" isActive={isActive('/client-portal')} testId="nav-client-portal" />
            </>
          ) : (
            <>
              <NavItem to="/dashboard" icon="🏠" label="Dashboard" isActive={isActive('/dashboard')} testId="nav-dashboard" />
              <NavItem to="/invoices" icon="📋" label="Invoices" isActive={isActive('/invoices')} testId="nav-invoices" />
              <NavItem to="/reports" icon="📊" label="Reports" isActive={isActive('/reports')} testId="nav-reports" />
              {isOwner && (
                <>
                  <NavItem to="/clients" icon="👥" label="Clients" isActive={isActive('/clients')} testId="nav-clients" />
                  <NavItem to="/users" icon="🔐" label="Users" isActive={isActive('/users')} testId="nav-users" />
                </>
              )}
            </>
          )}
        </nav>

        {/* Bottom section */}
        <div className="p-3 border-t space-y-1" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {isOwner && (
            <NavItem to="/settings" icon="⚙️" label="Settings" isActive={isActive('/settings')} testId="nav-settings" />
          )}

          {/* User card */}
          <div className="mt-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}>
                {(user?.email || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-white text-xs font-medium truncate" data-testid="user-email">{user?.email}</p>
                <p className="text-xs px-1.5 py-0.5 rounded-md inline-block mt-0.5 text-xs" style={roleBadgeStyle}>
                  {roleLabel}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full text-xs text-center py-1.5 rounded-lg transition-all font-medium"
              style={{ color: '#94a3b8', background: 'rgba(255,255,255,0.05)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#fca5a5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-with-sidebar flex-1 p-8 animate-fade-in">
        {children}
      </main>
    </div>
  );
};

export default Layout;